import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface AuthRole {
  id: number;
  slug: string;
  label: string;
  description: string;
  permissions: string[];
  is_system: boolean;
}

export interface AuthUser {
  id: number;
  email: string;
  name: string;
  is_active: boolean;
  role: AuthRole;
  created_at: string;
  updated_at: string;
  // Billing snapshot — populated by /api/auth/me + /api/billing/me.
  tokens_balance?: number;
  subscription_status?: string;
  subscription_plan_id?: number | null;
  subscription_expires_at?: string | null;
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  setSession: (token: string, user: AuthUser) => void;
  setUser: (user: AuthUser) => void;
  /** Merge partial billing fields onto the cached user (used by TokensBadge). */
  patchBilling: (patch: Partial<Pick<AuthUser, 'tokens_balance' | 'subscription_status' | 'subscription_plan_id' | 'subscription_expires_at'>>) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      setSession: (token, user) => set({ token, user }),
      setUser: (user) => set({ user }),
      patchBilling: (patch) =>
        set((state) => (state.user ? { user: { ...state.user, ...patch } } : {})),
      logout: () => set({ token: null, user: null }),
    }),
    { name: 'alphaconcreto-auth' },
  ),
);

export function isAdmin(user: AuthUser | null): boolean {
  return !!user && user.role.slug === 'admin';
}

/**
 * Check if the user's role grants a given permission. Returns true also when
 * the role has the wildcard `modules.all` (admin shortcut). `user` may be
 * null — returns false in that case.
 */
export function hasPermission(user: AuthUser | null, permission: string): boolean {
  if (!user) return false;
  const perms = user.role.permissions ?? [];
  if (perms.includes(permission)) return true;
  // Wildcard support: `modules.all` grants any `modules.*`
  if (permission.startsWith('modules.') && perms.includes('modules.all')) return true;
  return false;
}

/** True if the user has at least one of the listed permissions. */
export function hasAnyPermission(user: AuthUser | null, permissions: string[]): boolean {
  return permissions.some((p) => hasPermission(user, p));
}
